export type PlatformPhaseId = '0' | '1' | '2';

export interface PlatformPhase {
  id: PlatformPhaseId;
  label: string;
  shortLabel: string;
  description: string;
  pathSegment: string;
}

export const PLATFORM_PHASES: PlatformPhase[] = [
  {
    id: '0',
    label: 'Discover',
    shortLabel: 'Discover',
    description: 'Ingest repository, review artifacts, and baseline',
    pathSegment: 'phase-0',
  },
  {
    id: '1',
    label: 'Understand',
    shortLabel: 'Understand',
    description: 'Structural, behavioral, security, and business analysis',
    pathSegment: 'phase-1',
  },
  {
    id: '2',
    label: 'Operate',
    shortLabel: 'Operate',
    description: 'Incident workspace — investigate, fix, verify, learn',
    pathSegment: 'operate',
  },
];

/** Discover workspace tabs — engineering workflow names */
export type Phase0TabId =
  | 'repository'
  | 'artifacts'
  | 'changes'
  | 'review';

export const PHASE0_TABS: { id: Phase0TabId; label: string }[] = [
  { id: 'repository', label: 'Repository' },
  { id: 'artifacts', label: 'Artifacts' },
  { id: 'changes', label: 'Changes' },
  { id: 'review', label: 'Review' },
];

/** Legacy Phase 0 paths → canonical tab */
export const PHASE0_LEGACY_TAB_MAP: Record<string, Phase0TabId> = {
  upload: 'repository',
  verify: 'repository',
  workflow: 'repository',
  intake: 'repository',
  dependencies: 'artifacts',
  knowledge: 'artifacts',
  graph: 'artifacts',
  repo: 'artifacts',
  overview: 'artifacts',
  delta: 'changes',
  inventory: 'artifacts',
  scan: 'artifacts',
  artifacts: 'artifacts',
  baseline: 'review',
  report: 'review',
  handoff: 'review',
};

/** Wave A — simplified Phase 1 tabs */
export type Phase1TabId = 'insights' | 'graph' | 'knowledge';

export type Phase1LayerId = 'structural' | 'behavioral' | 'security' | 'business';

export const PHASE1_LAYERS: { id: Phase1LayerId; label: string }[] = [
  { id: 'structural', label: 'Structural' },
  { id: 'behavioral', label: 'Behavioral' },
  { id: 'security', label: 'Security' },
  { id: 'business', label: 'Business' },
];

export const PHASE1_TABS: { id: Phase1TabId; label: string }[] = [
  { id: 'insights', label: 'Insights' },
  { id: 'graph', label: 'Graph' },
  { id: 'knowledge', label: 'Knowledge' },
];

// 'ask' was a real canonical tab before the Milestone 5 cutover (not just a
// segment alias) — App.tsx also has a hard <Route path="ask" .../> redirect
// for direct bookmarks/links, since this map only rewrites segments reached
// via phase1LegacyRedirect, not literal router paths.
export const PHASE1_LEGACY_TAB_MAP: Record<string, Phase1TabId | Phase1LayerId> = {
  layers: 'insights',
  structural: 'insights',
  behavioral: 'insights',
  security: 'insights',
  business: 'insights',
  ask: 'knowledge',
};

/** Wave A — simplified Phase 2 tabs (2) */
export type Phase2TabId = 'change-requests' | 'checks';

export const PHASE2_TABS: { id: Phase2TabId; label: string }[] = [
  { id: 'change-requests', label: 'Change requests' },
  { id: 'checks', label: 'Checks & readiness' },
];

export const PHASE2_LEGACY_TAB_MAP: Record<string, Phase2TabId> = {
  'governance-checks': 'checks',
  execution: 'checks',
  verification: 'checks',
};

/** Operate route segments (all preserved for deep links / demoted secondary views) */
export type OperateTabId =
  | 'overview'
  | 'incidents'
  | 'external-changes'
  | 'runtime-logs'
  | 'reported'
  | 'requests'
  | 'missions'
  | 'incident'
  | 'investigations'
  | 'executions'
  | 'executors'
  | 'replay'
  | 'test-environment'
  | 'engineering'
  | 'fixes'
  | 'learning';

/**
 * First-level Operate domains — SDLC pipeline order.
 * Investigation is an internal mode of Incidents (not a top-level tab).
 * Requests is the proactive Engineering Intelligence entry point.
 */
export type OperateDomainId =
  | 'incidents'
  | 'requests'
  | 'engineering'
  | 'executions'
  | 'quality';

export interface OperateNavItem {
  id: OperateTabId;
  label: string;
  description?: string;
}

export interface OperateDomain {
  id: OperateDomainId;
  label: string;
  description?: string;
  /** Default route when selecting the domain */
  defaultTab: OperateTabId;
  /** Secondary in-domain views (not Operate top-level) */
  secondary: OperateNavItem[];
}

/**
 * Operate domains — lifecycle order:
 * Incidents (reactive) → Requests (proactive) → Engineering (plan, approve) →
 * Testing (verify the approved package on the run's own working copy) →
 * Executions (promote the accepted package into the project repository).
 */
export const OPERATE_DOMAINS: OperateDomain[] = [
  {
    id: 'incidents',
    label: 'Incidents',
    description: 'Detection, investigation, RCA, evidence, recommendations',
    defaultTab: 'incidents',
    secondary: [
      { id: 'incidents', label: 'Inbox', description: 'Email and connected-source incident queue' },
      {
        id: 'runtime-logs',
        label: 'Runtime logs',
        description: 'Upload application logs and extract errors, alerts, and bugs',
      },
      { id: 'reported', label: 'Reported', description: 'Manually reported incidents' },
    ],
  },
  {
    id: 'requests',
    label: 'Requests',
    description: 'Proactive engineering work — features, modernization, refactoring, and more',
    defaultTab: 'requests',
    secondary: [],
  },
  {
    id: 'engineering',
    label: 'Engineering',
    description: 'Operational remediation or engineering plan — handoff, approve, recover',
    defaultTab: 'engineering',
    // No secondary sub-tab — a single flowing page (the Learning sub-tab
    // was removed by request; Learning content stays reachable directly at
    // /operate/learning for anything that still links there).
    secondary: [],
  },
  {
    id: 'quality',
    label: 'Testing',
    description: 'Test an approved package before it is executed — run, review, accept or waive the results',
    defaultTab: 'test-environment',
    // No secondary sub-tab by request — Replay & Certification stays reachable
    // directly at /operate/replay for anything that still links there.
    secondary: [],
  },
  {
    id: 'executions',
    label: 'Apply',
    description: 'Execute accepted packages into the project repository — and what ran to recover or deploy',
    defaultTab: 'executions',
    // No secondary sub-tab by request — one flowing page instead of an
    // Executions/Executors toggle. The Executors page (executor readiness)
    // stays reachable directly at /operate/executors.
    secondary: [],
  },
];

export const OPERATE_TAB_TO_DOMAIN: Record<OperateTabId, OperateDomainId> = {
  /** Legacy Overview tab — redirect target is Incidents */
  overview: 'incidents',
  incidents: 'incidents',
  'runtime-logs': 'incidents',
  reported: 'incidents',
  missions: 'incidents',
  incident: 'incidents',
  /** Legacy Investigations tab → Incidents domain (investigation is in-workspace) */
  investigations: 'incidents',
  /** Legacy External Changes tab — soft-redirected away from Operate nav */
  'external-changes': 'incidents',
  requests: 'requests',
  executions: 'executions',
  executors: 'executions',
  replay: 'quality',
  'test-environment': 'quality',
  engineering: 'engineering',
  fixes: 'engineering',
  learning: 'engineering',
};

/** @deprecated Prefer OPERATE_DOMAINS — kept for any leftover group consumers */
export type OperateNavGroup = {
  id: string;
  label: string | null;
  items: OperateNavItem[];
};

/** Flat list of all route tabs (primary + demoted secondary) */
export const OPERATE_TABS: { id: OperateTabId; label: string }[] = (() => {
  const seen = new Set<OperateTabId>();
  const out: { id: OperateTabId; label: string }[] = [];
  for (const d of OPERATE_DOMAINS) {
    if (!seen.has(d.defaultTab)) {
      seen.add(d.defaultTab);
      out.push({ id: d.defaultTab, label: d.label });
    }
    for (const s of d.secondary) {
      if (!seen.has(s.id)) {
        seen.add(s.id);
        out.push({ id: s.id, label: s.label });
      }
    }
  }
  return out;
})();

export function resolveOperateTab(pathname: string): OperateTabId {
  const parts = pathname.split('/');
  const idx = parts.indexOf('operate');
  const seg = (idx >= 0 ? parts[idx + 1] : 'incidents') as OperateTabId;
  if (seg === 'overview' || seg === 'external-changes') return 'incidents';
  if (seg in OPERATE_TAB_TO_DOMAIN) return seg;
  return OPERATE_TABS.some((t) => t.id === seg) ? seg : 'incidents';
}

export function resolveOperateDomain(tab: OperateTabId): OperateDomain {
  const id = OPERATE_TAB_TO_DOMAIN[tab] ?? 'incidents';
  return OPERATE_DOMAINS.find((d) => d.id === id) ?? OPERATE_DOMAINS[0];
}

/** @deprecated Stages removed — use OperateTabId */
export type IncidentStageId = OperateTabId;

/** @deprecated */
export const INCIDENT_STAGES = OPERATE_TABS;

export const OPERATE_LEGACY_TAB_MAP: Record<string, OperateTabId> = {
  analyze: 'incidents',
  customize: 'incident',
  enhance: 'learning',
  health: 'test-environment',
  impact: 'incident',
  notifications: 'incidents',
  'test-env': 'test-environment',
  drift: 'test-environment',
  verification: 'test-environment',
  enhancements: 'learning',
  logs: 'runtime-logs',
  edr: 'incident',
  alerts: 'incident',
  overview: 'incidents',
  incidents: 'incidents',
  'external-changes': 'incidents',
  'runtime-logs': 'runtime-logs',
  reported: 'reported',
  investigations: 'incidents',
  requests: 'requests',
  'engineering-requests': 'requests',
  'engineering-missions': 'engineering',
  engineering: 'engineering',
  fixes: 'engineering',
  validation: 'test-environment',
  deployments: 'test-environment',
  learning: 'learning',
  timeline: 'incidents',
  missions: 'incidents',
  'mission-control': 'incidents',
  certification: 'replay',
  replay: 'replay',
  executions: 'executions',
  executors: 'executors',
  changes: 'engineering',
  runtime: 'test-environment',
  history: 'learning',
  'change-requests': 'engineering',
  checks: 'test-environment',
  evidence: 'incident',
  'root-cause': 'incidents',
  patch: 'engineering',
  deployment: 'test-environment',
};

export function projectPhasePath(projectId: string, phase: PlatformPhaseId | '3'): string {
  const def = PLATFORM_PHASES.find((p) => p.id === phase);
  if (phase === '0') return `/projects/${projectId}/phase-0/repository`;
  if (phase === '1') return `/projects/${projectId}/phase-1/insights`;
  // Platform Operate is "2"; lifecycle workflow also uses "3" for operate.
  if (phase === '2' || phase === '3') return `/projects/${projectId}/operate/incidents`;
  if (!def) return `/projects/${projectId}`;
  return `/projects/${projectId}/${def.pathSegment}`;
}

/** Path to Phase 0 repo tab with a file highlight query. */
export function phase0RepoFilePath(projectId: string, filePath: string): string {
  return `/projects/${projectId}/phase-0/artifacts?file=${encodeURIComponent(filePath)}`;
}

/** Path to Phase 1 graph explorer with a focused node. */
export function phase0GraphNodePath(projectId: string, nodeId: string): string {
  return `/projects/${projectId}/phase-1/graph/explorer?node=${encodeURIComponent(nodeId)}`;
}

export function phase0TabPath(projectId: string, tab: Phase0TabId | string): string {
  const resolved = (PHASE0_LEGACY_TAB_MAP[tab] ?? tab) as Phase0TabId;
  return `/projects/${projectId}/phase-0/${resolved}`;
}

/** Path to the project's file/folder tree — the Discover → Artifacts tab. */
export function artifactTreePath(projectId?: string): string {
  if (!projectId) return '/projects/all';
  return phase0TabPath(projectId, 'artifacts');
}

export function phase1TabPath(projectId: string, tab: Phase1TabId): string {
  return `/projects/${projectId}/phase-1/${tab}`;
}

export function phase1GraphExplorerPath(projectId: string): string {
  return `/projects/${projectId}/phase-1/graph/explorer`;
}

export function phase1LayerPath(projectId: string, layer: Phase1LayerId): string {
  return `/projects/${projectId}/phase-1/layers/${layer}`;
}

export function operateTabPath(
  projectId: string,
  tab: OperateTabId,
  incidentId?: string | null,
): string {
  const base = `/projects/${projectId}/operate/${tab}`;
  if (!incidentId) return base;
  return `${base}?incident=${encodeURIComponent(incidentId)}`;
}

/**
 * Path to the Engineering domain scoped to a specific Engineering Request's
 * mission (a change request, not an AMS incident). The id is the Engineering
 * Request's own id (`requestIdOf(r)`), not its `mission_reference` — the
 * workspace resolves the change request internally so the "Current Context"
 * banner can show the request's own title/status/lifecycle. Parallel to the
 * `?incident=` convention used for incident-derived missions.
 */
export function operateEngineeringRequestPath(projectId: string, requestId: string): string {
  return `/projects/${projectId}/operate/engineering?request=${encodeURIComponent(requestId)}`;
}

/** A unified reference into the Engineering workspace — Incident today, more mission types later. */
export type EngineeringContextRef =
  | { kind: 'incident'; id: string }
  | { kind: 'request'; id: string }
  | null;

/** Path builder for the unified EngineeringContext selector — replaces ad hoc ?incident=/?mission= call sites. */
export function operateEngineeringContextPath(projectId: string, ref: EngineeringContextRef): string {
  if (!ref) return `/projects/${projectId}/operate/engineering`;
  if (ref.kind === 'request') return operateEngineeringRequestPath(projectId, ref.id);
  return operateTabPath(projectId, 'engineering', ref.id);
}

/** Execution-tab counterpart of operateEngineeringContextPath — same unified ref, `executions` tab. */
export function operateExecutionsContextPath(projectId: string, ref: EngineeringContextRef): string {
  return operateContextPath(projectId, 'executions', ref);
}

/**
 * Stay-on-tab path for the unified mission selector. Selecting a request or
 * incident must never bounce the operator to Engineering when they are on
 * Test / Learning / Executions / etc.
 */
export function operateContextPath(
  projectId: string,
  tab: OperateTabId,
  ref: EngineeringContextRef,
): string {
  if (!ref) return `/projects/${projectId}/operate/${tab}`;
  if (ref.kind === 'request') {
    return `${operateTabPath(projectId, tab)}?request=${encodeURIComponent(ref.id)}`;
  }
  return operateTabPath(projectId, tab, ref.id);
}

export function operateDomainPath(
  projectId: string,
  domainId: OperateDomainId,
  incidentId?: string | null,
): string {
  const domain = OPERATE_DOMAINS.find((d) => d.id === domainId) ?? OPERATE_DOMAINS[0];
  return operateTabPath(projectId, domain.defaultTab, incidentId);
}

export type OperateMissionQuery = {
  incidentId?: string | null;
  requestId?: string | null;
};

/**
 * Preserve ?request= (Engineering Request missions) or ?incident= when switching
 * Operate tabs. Top-level domain nav previously only forwarded incidentId, which
 * dropped request-scoped missions on Testing / Executions / etc.
 */
export function operateTabNavigationPath(
  projectId: string,
  tab: OperateTabId,
  query: OperateMissionQuery = {},
): string {
  const incidentId = query.incidentId ?? null;
  const requestId = query.requestId ?? null;
  if (requestId && !incidentId) {
    return operateContextPath(projectId, tab, { kind: 'request', id: requestId });
  }
  return operateTabPath(projectId, tab, incidentId);
}

export function operateDomainNavigationPath(
  projectId: string,
  domainId: OperateDomainId,
  query: OperateMissionQuery = {},
): string {
  const domain = OPERATE_DOMAINS.find((d) => d.id === domainId) ?? OPERATE_DOMAINS[0];
  return operateTabNavigationPath(projectId, domain.defaultTab, query);
}

export function investigationPath(projectId: string, incidentId?: string): string {
  return operateTabPath(projectId, 'incidents', incidentId);
}

/** Canonical Incidents domain path with optional incident selection */
export function incidentsDomainPath(
  projectId: string,
  opts?: { incidentId?: string | null; /** @deprecated section tabs removed — ignored */ section?: string },
): string {
  const base = `/projects/${projectId}/operate/incidents`;
  const params = new URLSearchParams();
  if (opts?.incidentId) params.set('incident', opts.incidentId);
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

/** @deprecated Use operateTabPath — maps old stage names to the five Operate tabs */
export function incidentStagePath(
  projectId: string,
  incidentId: string,
  stage: string = 'incident',
): string {
  const map: Record<string, OperateTabId> = {
    overview: 'incidents',
    evidence: 'incident',
    'root-cause': 'incident',
    incident: 'incident',
    patch: 'engineering',
    fixes: 'engineering',
    engineering: 'engineering',
    validation: 'test-environment',
    deployment: 'test-environment',
    'test-environment': 'test-environment',
    learning: 'learning',
  };
  return operateTabPath(projectId, map[stage] ?? 'incident', incidentId);
}

export function phase2TabPath(projectId: string, _tab: Phase2TabId = 'change-requests'): string {
  return operateTabPath(projectId, 'engineering');
}

export function detectPhaseFromPath(pathname: string): PlatformPhaseId {
  if (pathname.includes('/phase-1')) return '1';
  if (pathname.includes('/operate') || pathname.includes('/ams')) return '2';
  if (pathname.includes('/phase-2') || pathname.includes('/governance') || pathname.includes('/modernization')) {
    return '2';
  }
  if (pathname.includes('/phase-0') || pathname.includes('/ingestion')) return '0';
  return '0';
}

/** Resolve legacy segment to canonical path (or null if already canonical). */
export function phase0LegacyRedirect(projectId: string, segment: string): string | null {
  const mapped = PHASE0_LEGACY_TAB_MAP[segment];
  if (!mapped || mapped === segment) return null;
  return phase0TabPath(projectId, mapped);
}

export function phase1LegacyRedirect(projectId: string, segment: string): string | null {
  if (segment in PHASE1_LEGACY_TAB_MAP) {
    const mapped = PHASE1_LEGACY_TAB_MAP[segment];
    if (mapped === 'insights' || mapped === 'knowledge') {
      return phase1TabPath(projectId, mapped);
    }
    return phase1TabPath(projectId, 'insights');
  }
  return null;
}

export function phase2LegacyRedirect(projectId: string, segment: string): string | null {
  const mapped = PHASE2_LEGACY_TAB_MAP[segment];
  if (!mapped) return null;
  return phase2TabPath(projectId, mapped);
}

export function phase3LegacyRedirect(projectId: string, segment: string): string | null {
  const mapped = OPERATE_LEGACY_TAB_MAP[segment];
  if (!mapped) return null;
  return operateTabPath(projectId, mapped);
}

export function operateLegacyRedirect(projectId: string, segment: string): string | null {
  return phase3LegacyRedirect(projectId, segment);
}

// --- P1 Milestone 1 — Governance ------------------------------------------

export type GovernanceTabId =
  | 'policies'
  | 'pending-approvals'
  | 'calendar'
  | 'history'
  | 'team';

export function governanceTabPath(projectId: string, tab: GovernanceTabId): string {
  return `/projects/${projectId}/governance/${tab}`;
}
